import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Tabs, Tag, Typography } from 'antd';
import { capitalCase } from 'change-case';
// Workspace Directory is intentionally parked until its next product pass.
// import { CirclePile } from 'lucide-react';
import { TbArrowLeft, TbArrowRight } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { EmptySection, VirtualizedList as List, PlatformAlert, SkeletonList, SmallTag } from '@/shared';
// import { SmallTooltip, WorkspaceDirectoryModal } from '@/shared';
import { useMemberStore } from '@/stores';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { WorkspaceInvitationSummary } from '@/stores/database/aliases/invitations/types';
import { Member, MemberPermissionType, MemberStatusType } from '@/types';

import MembersListHeader from './components/MembersListHeader';
import MembersListItem from './components/MembersListItem';
import WorkspaceInvitationListItem from './components/WorkspaceInvitationListItem';

const Members: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [activeKey, setActiveKey] = useState<MemberStatusType | string>(MemberStatusType.ACTIVE);
    const [hideCount] = useState(false);
    const [selectedPermissionFilter] = useState<string[]>([]);
    // const [workspaceDirectoryOpen, setWorkspaceDirectoryOpen] = useState(false);

    const { selectedMember: member, fetchMembers, members } = useMemberStore();
    const { fetchWorkspaceInvitations, workspaceInvitations } = useInvitationStore();
    const { token } = useAntDesignTheme();

    useEffect(() => {
        let mounted = true;

        void Promise.all([fetchMembers(), fetchWorkspaceInvitations()]).finally(() => {
            if (mounted) {
                setLoading(false);
            }
        });

        return () => {
            mounted = false;
        };
    }, [fetchMembers, fetchWorkspaceInvitations]);

    const filteredMembers = useMemo(() => {
        if (!query.trim()) {
            return members;
        }
        const q = query.toLowerCase();
        return members.filter(
            (m) =>
                m?.user?.firstName?.toLowerCase().includes(q) ||
                m?.user?.lastName?.toLowerCase().includes(q) ||
                m?.user?.emailAddress?.toLowerCase().includes(q),
        );
    }, [members, query]);

    const sortByCreated = (a: Member, b: Member) => Number(new Date(a.createdAt)) - Number(new Date(b.createdAt));

    const activeMembers = useMemo(
        () => filteredMembers.filter((m) => m.status === MemberStatusType.ACTIVE).sort(sortByCreated),
        [filteredMembers],
    );
    const filteredWorkspaceInvitations = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        const matchingInvitations = normalizedQuery
            ? workspaceInvitations.filter(
                  (invitation) =>
                      invitation.emailAddress.toLowerCase().includes(normalizedQuery) ||
                      invitation.permission.toLowerCase().includes(normalizedQuery) ||
                      invitation.customRole?.name?.toLowerCase().includes(normalizedQuery),
              )
            : workspaceInvitations;

        return [...matchingInvitations].sort(
            (left, right) => Number(new Date(right.createdAt)) - Number(new Date(left.createdAt)),
        );
    }, [query, workspaceInvitations]);
    const deactivatedMembers = useMemo(
        () => filteredMembers.filter((m) => m.status === MemberStatusType.DEACTIVATED).sort(sortByCreated),
        [filteredMembers],
    );
    const blockedMembers = useMemo(
        () => filteredMembers.filter((m) => m.status === MemberStatusType.BLOCKED).sort(sortByCreated),
        [filteredMembers],
    );

    const { workspacePermissionOrder, workspacePermissionStatusIconMap } = useMaps();

    const sortedMembers = (arr: Member[]) =>
        [...arr].sort(
            (a, b) =>
                (a.permission ? (workspacePermissionOrder[a.permission] ?? 99) : 99) -
                (b.permission ? (workspacePermissionOrder[b.permission] ?? 99) : 99),
        );

    const disabled =
        member?.permission !== MemberPermissionType.ADMIN && member?.permission !== MemberPermissionType.OWNER;

    const MembersList = ({ dataSource }: { dataSource: Member[] }) => {
        return dataSource?.length !== 0 && !loading ? (
            <List
                dataSource={dataSource}
                renderItem={(member: Member) => <MembersListItem key={member?.id as string} member={member} />}
                style={{
                    height: '25rem',
                    overflowY: 'auto',
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
            />
        ) : loading ? (
            <>
                <SkeletonList listLength={5} maxHeight='20rem' minHeight='20rem' />
            </>
        ) : (
            <EmptySection
                height='22.5rem'
                icon={workspacePermissionStatusIconMap[activeKey as MemberStatusType]}
                label={`No ${activeKey ? capitalCase(activeKey) : ''} Workspace Members`}
                description='Workspace members with this status will appear here.'
                extra={
                    activeKey !== MemberStatusType.ACTIVE ? (
                        <Button onClick={() => setActiveKey(MemberStatusType.ACTIVE)}>
                            <TbArrowLeft />
                            Manage Active Users
                        </Button>
                    ) : null
                }
            />
        );
    };

    const InvitationsList = ({ dataSource }: { dataSource: WorkspaceInvitationSummary[] }) => {
        return dataSource.length !== 0 && !loading ? (
            <List
                dataSource={dataSource}
                renderItem={(invitation: WorkspaceInvitationSummary) => (
                    <WorkspaceInvitationListItem key={invitation.id} invitation={invitation} />
                )}
                style={{
                    height: '25rem',
                    overflowY: 'auto',
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
            />
        ) : loading ? (
            <SkeletonList listLength={5} maxHeight='20rem' minHeight='20rem' />
        ) : (
            <EmptySection
                height='22.5rem'
                icon={workspacePermissionStatusIconMap[MemberStatusType.PENDING]}
                label='No Pending Workspace Invitations'
                description='Workspace invitations awaiting acceptance will appear here.'
                extra={
                    <Button onClick={() => setActiveKey(MemberStatusType.ACTIVE)}>
                        <TbArrowLeft />
                        Manage Active Users
                    </Button>
                }
            />
        );
    };

    const tabItems = [
        {
            key: MemberStatusType.ACTIVE,
            label: (
                <span>
                    Active
                    {!hideCount && activeMembers?.length > 0 && <SmallTag message={activeMembers?.length} />}
                </span>
            ),
            // eslint-disable-next-line react-hooks/static-components
            children: <MembersList dataSource={sortedMembers(activeMembers)} />,
        },
        {
            key: MemberStatusType.PENDING,
            label: (
                <span>
                    Invited
                    {!disabled && !hideCount && filteredWorkspaceInvitations.length > 0 && (
                        <SmallTag message={filteredWorkspaceInvitations.length} />
                    )}
                </span>
            ),
            // eslint-disable-next-line react-hooks/static-components
            children: <InvitationsList dataSource={filteredWorkspaceInvitations} />,
            disabled,
        },
        {
            key: MemberStatusType.DEACTIVATED,
            label: (
                <span>
                    Deactivated
                    {!disabled && !hideCount && deactivatedMembers?.length > 0 && (
                        <SmallTag message={deactivatedMembers?.length} />
                    )}
                </span>
            ),
            // eslint-disable-next-line react-hooks/static-components
            children: <MembersList dataSource={sortedMembers(deactivatedMembers)} />,
            disabled,
        },
        {
            key: MemberStatusType.BLOCKED,
            label: (
                <span>
                    Blocked
                    {!disabled && !hideCount && blockedMembers?.length > 0 && (
                        <SmallTag message={blockedMembers?.length} />
                    )}
                </span>
            ),
            // eslint-disable-next-line react-hooks/static-components
            children: <MembersList dataSource={sortedMembers(blockedMembers)} />,
            disabled,
        },
    ];

    return (
        <>
            <Typography.Title level={4}>Members</Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage all users associated with your workspace here.
            </Typography.Paragraph>

            <MembersListHeader query={query} setQuery={setQuery} members={members} setMembers={() => {}} />

            {selectedPermissionFilter.length > 0 && (
                <Row style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                    <Col span={24}>
                        {selectedPermissionFilter.map((filter) => (
                            <Tag style={{ borderColor: 'transparent' }} key={filter}>
                                {filter}
                            </Tag>
                        ))}
                    </Col>
                </Row>
            )}

            {/* Workspace Directory tabBarExtraContent launcher intentionally parked.
            <SmallTooltip title='Open Workspace Directory'>
                <Button
                    aria-label='Open Workspace Directory'
                    icon={<CirclePile size='1rem' />}
                    size='small'
                    type='text'
                    onClick={() => setWorkspaceDirectoryOpen(true)}
                />
            </SmallTooltip> */}
            <Tabs
                size='small'
                activeKey={activeKey}
                onChange={setActiveKey}
                tabBarStyle={{ marginBottom: 0 }}
                style={{ height: '25rem', borderBottom: `1px solid ${token.colorBorder}` }}
                items={tabItems}
            />
            {/* Workspace Directory modal intentionally parked.
            <WorkspaceDirectoryModal open={workspaceDirectoryOpen} onClose={() => setWorkspaceDirectoryOpen(false)} /> */}
            <PlatformAlert
                styles={{ marginTop: '1rem' }}
                status='info'
                title='Need more seats?'
                description={
                    <>
                        Add them to your existing plan in{' '}
                        <Link style={{ fontWeight: 500 }} to='/settings/account/plans/billing'>
                            Settings › Account › Plans
                            <TbArrowRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.25rem' }} />
                        </Link>
                    </>
                }
            />
        </>
    );
};

export default Members;
